import api from "../api";


const ROOT = "/api/unified-journeys";
let capabilitiesPromise = null;

export function mutationKey(prefix = "journey") {
  const random = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`;
}

export async function getJourneyCapabilities({ refresh = false } = {}) {
  if (!capabilitiesPromise || refresh) {
    capabilitiesPromise = api
      .get(`${ROOT}/capabilities/`)
      .then((response) => response.data)
      .catch((error) => {
        capabilitiesPromise = null;
        throw error;
      });
  }
  return capabilitiesPromise;
}

export async function listJourneys(params = {}, signal) {
  const response = await api.get(`${ROOT}/journeys/`, { params, signal });
  return response.data;
}

export async function getJourneySummary(signal) {
  const response = await api.get(`${ROOT}/journeys/summary/`, { signal });
  return response.data;
}

export async function getJourneyDashboard(signal) {
  const response = await api.get(`${ROOT}/journeys/dashboard/`, { signal });
  return response.data;
}

export async function getJourney(publicId, signal) {
  const response = await api.get(`${ROOT}/journeys/${publicId}/`, { signal });
  return response.data;
}

export async function startJourney(payload) {
  const response = await api.post(`${ROOT}/journeys/`, payload);
  return response.data;
}

export async function sendProposal(publicId, payload, key = mutationKey("proposal")) {
  const response = await api.post(`${ROOT}/journeys/${publicId}/proposals/`, payload, {
    headers: { "Idempotency-Key": key },
  });
  return response.data;
}

export async function respondProposal(proposalId, action) {
  const response = await api.post(`${ROOT}/proposals/${proposalId}/respond/`, { action });
  return response.data;
}

export async function proposeAppointment(publicId, payload, key = mutationKey("appointment")) {
  const response = await api.post(`${ROOT}/journeys/${publicId}/appointments/`, payload, {
    headers: { "Idempotency-Key": key },
  });
  return response.data;
}

export async function respondAppointment(appointmentId, action) {
  const response = await api.post(`${ROOT}/appointments/${appointmentId}/respond/`, { action });
  return response.data;
}

export async function transitionJourney(publicId, action, expectedVersion, details = {}) {
  const response = await api.post(`${ROOT}/journeys/${publicId}/transition/`, {
    action,
    expected_version: expectedVersion,
    ...details,
  });
  return response.data;
}

export async function cancelJourney(publicId, payload) {
  const response = await api.post(`${ROOT}/journeys/${publicId}/cancel/`, payload);
  return response.data;
}

export async function openJourneyIncident(publicId, payload, key = mutationKey("incident")) {
  const response = await api.post(`${ROOT}/journeys/${publicId}/incident/`, payload, {
    headers: { "Idempotency-Key": key },
  });
  return response.data;
}

export async function leaveJourneyReview(publicId, payload) {
  const response = await api.post(`${ROOT}/journeys/${publicId}/reviews/`, payload);
  return response.data;
}

export async function listJourneySources(kind, params = {}, signal) {
  const endpoint = kind === "job" ? "jobs" : kind === "market" ? "market" : "services";
  const response = await api.get(`${ROOT}/sources/${endpoint}/`, { params, signal });
  return response.data;
}

export async function createJourneySource(kind, payload, key = mutationKey(`source-create-${kind}`)) {
  const endpoint = kind === "job" ? "jobs" : kind === "market" ? "market" : "services";
  const response = await api.post(`${ROOT}/sources/${endpoint}/`, payload, {
    headers: { "Idempotency-Key": key },
  });
  return response.data;
}

export async function getJourneySource(kind, sourceId, signal) {
  const response = await api.get(`${ROOT}/sources/${encodeURIComponent(kind)}/${encodeURIComponent(sourceId)}/`, { signal });
  return response.data;
}

export async function updateJourneySource(kind, sourceId, payload, version) {
  const headers = version == null ? {} : { "If-Match": `draft-revision-${version}` };
  const response = await api.patch(
    `${ROOT}/sources/${encodeURIComponent(kind)}/${encodeURIComponent(sourceId)}/`,
    payload,
    { headers }
  );
  return response.data;
}

export async function getJourneySourcePreview(kind, sourceId, signal) {
  const response = await api.get(`${ROOT}/sources/${encodeURIComponent(kind)}/${encodeURIComponent(sourceId)}/preview/`, { signal });
  return response.data;
}

export async function getJourneySourceVersions(kind, sourceId, signal) {
  const response = await api.get(`${ROOT}/sources/${encodeURIComponent(kind)}/${encodeURIComponent(sourceId)}/versions/`, { signal });
  return response.data;
}

export async function listMyContents(params = {}, signal) {
  const response = await api.get(`${ROOT}/contents/mine/`, { params, signal });
  return response.data;
}

export async function actionJourneySource(kind, sourceId, action, payload = {}, key = mutationKey(`source-${action}`)) {
  const response = await api.post(
    `${ROOT}/sources/${encodeURIComponent(kind)}/${encodeURIComponent(sourceId)}/actions/${encodeURIComponent(action)}/`,
    payload,
    { headers: { "Idempotency-Key": key } }
  );
  return response.data;
}
